import {
  AiTransformJob,
  AiTransformSet,
  AiTransformSetStartInput,
  AiTransformStartInput,
  Appointment,
  AppointmentInput,
  AppointmentStatus,
  AudienceReport,
  AuthResponse,
  LoginInput,
  Product,
} from '../types';
import {
  mockCreateAppointment,
  mockCreateProduct,
  mockDeleteAppointment,
  mockDeleteProduct,
  mockGetAppointments,
  mockGetBookedSlots,
  mockLogin,
  mockProducts,
  mockUpdateAppointment,
  mockUpdateAppointmentStatus,
  mockUpdateProduct,
} from './mocks';

export interface BookedSlot {
  date: string;
  timeSlot: string;
}

export type AppointmentPatch = Partial<Pick<Appointment, 'notes' | 'appointmentDate' | 'timeSlot'>>;

export type ProductInput = Omit<Product, 'id' | 'createdAt' | 'updatedAt'>;

const useMocks = import.meta.env.VITE_USE_MOCKS === 'true';
const baseUrl = import.meta.env.VITE_API_BASE_URL;

if (!useMocks && !baseUrl) {
  throw new Error('Missing VITE_API_BASE_URL environment variable');
}

let authToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

export { ConflictError, UnauthorizedError } from './errors';
import { ConflictError, UnauthorizedError } from './errors';

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  const response = await fetch(`${baseUrl}${path}`, { ...init, headers });

  if (response.status === 401) {
    if (unauthorizedHandler) unauthorizedHandler();
    throw new UnauthorizedError();
  }

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body && typeof body.message === 'string') {
        message = body.message;
      }
    } catch {
      // body wasn't JSON — keep the status fallback
    }
    if (response.status === 409) {
      throw new ConflictError(message);
    }
    throw new Error(message);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

function delay<T>(value: T, ms = 300): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export function getProducts(): Promise<Product[]> {
  if (useMocks) {
    return delay([...mockProducts]);
  }
  return apiFetch<Product[]>('/products');
}

export function createProduct(input: ProductInput): Promise<Product> {
  if (useMocks) {
    return delay(mockCreateProduct(input));
  }
  return apiFetch<Product>('/products', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export interface TranslateProductInput {
  sourceLocale: string;
  title: string;
  description: string;
}

export interface TranslateProductResult {
  translations: Record<string, { title?: string; description?: string }>;
}

/**
 * ADMIN: machine-translate a product's source copy (title + description)
 * into the other supported locales via the backend's Gemini text endpoint.
 * The admin reviews + edits the result before saving — it's an assist.
 *
 * Mock mode returns a `[locale] …`-prefixed stub for every target so the
 * admin form is exercisable offline without a backend / API key.
 */
export function translateProduct(
  input: TranslateProductInput,
): Promise<TranslateProductResult> {
  if (useMocks) {
    const source = input.sourceLocale || 'fr';
    const targets = ['ar', 'fr', 'en'].filter((l) => l !== source);
    const translations: TranslateProductResult['translations'] = {};
    for (const l of targets) {
      translations[l] = {
        title: `[${l}] ${input.title}`,
        description: `[${l}] ${input.description}`,
      };
    }
    return delay({ translations });
  }
  return apiFetch<TranslateProductResult>('/admin/products/translate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateProduct(id: string, input: ProductInput): Promise<Product> {
  if (useMocks) {
    return delay(mockUpdateProduct(id, input));
  }
  return apiFetch<Product>(`/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function deleteProduct(id: string): Promise<void> {
  if (useMocks) {
    mockDeleteProduct(id);
    return delay(undefined);
  }
  return apiFetch<void>(`/products/${id}`, { method: 'DELETE' });
}

export function createAppointment(input: AppointmentInput): Promise<Appointment> {
  if (useMocks) {
    return delay(mockCreateAppointment(input));
  }
  return apiFetch<Appointment>('/appointments', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getBookedSlots(): Promise<BookedSlot[]> {
  if (useMocks) {
    return delay(mockGetBookedSlots());
  }
  return apiFetch<BookedSlot[]>('/appointments/booked-slots');
}

export function getAppointments(): Promise<Appointment[]> {
  if (useMocks) {
    return delay(mockGetAppointments());
  }
  return apiFetch<Appointment[]>('/appointments');
}

export function updateAppointmentStatus(
  id: string,
  status: AppointmentStatus,
): Promise<Appointment> {
  if (useMocks) {
    return delay(mockUpdateAppointmentStatus(id, status));
  }
  return apiFetch<Appointment>(`/appointments/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function login(input: LoginInput): Promise<AuthResponse> {
  if (useMocks) {
    return delay(mockLogin(input));
  }
  return apiFetch<AuthResponse>('/auth/login', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateAppointment(id: string, patch: AppointmentPatch): Promise<Appointment> {
  if (useMocks) {
    return delay(mockUpdateAppointment(id, patch));
  }
  return apiFetch<Appointment>(`/appointments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export function deleteAppointment(id: string): Promise<void> {
  if (useMocks) {
    mockDeleteAppointment(id);
    return delay(undefined);
  }
  return apiFetch<void>(`/appointments/${id}`, { method: 'DELETE' });
}

export interface UploadedImage {
  url: string;
  bytes: number;
  contentType: string;
}

/**
 * Turns a server-returned image URL (e.g. {@code /images/abc.jpg}) into
 * something the browser can fetch:
 *   - {@code http(s)://...} → returned untouched
 *   - {@code blob:...} → returned untouched (local file previews)
 *   - relative paths starting with {@code /} → prefixed with the API host
 *     (baseUrl with the trailing /api stripped) so they resolve against the
 *     back, not the front-dev server
 *   - anything else (including {@code data:}, {@code javascript:},
 *     {@code file:}, etc.) → returned untouched but callers should treat
 *     these as untrusted. Allowing {@code data:} previously was a defence-
 *     in-depth gap: a compromised admin could paste a data URI carrying
 *     scripted SVG content. Now we explicitly forbid {@code data:} so it
 *     becomes a broken image rather than a potential exploit vector.
 *
 * In production, the front and back share the same origin, so the prefix is
 * effectively empty and the relative path resolves correctly natively.
 */
export function resolveImageUrl(raw: string): string {
  if (!raw) return raw;
  // Explicitly drop data: / javascript: / file: / vbscript: payloads —
  // they pass React's text-escape but can carry script content under
  // certain elements (SVG <use>, <object>, etc.).
  if (/^(data:|javascript:|file:|vbscript:)/i.test(raw)) return '';
  if (/^(https?:|blob:)/i.test(raw)) return raw;
  if (!raw.startsWith('/')) return raw;
  if (!baseUrl) return raw;
  const apiHost = baseUrl.replace(/\/api\/?$/, '');
  return `${apiHost}${raw}`;
}

/**
 * Multipart upload to /api/uploads/image. Returns the public URL the back
 * has stored the file under; the front then drops that URL into
 * {@code product.imageUrl} (or pushes it into {@code images[]} for gallery).
 *
 * Mock mode reads the file as a data URL so the existing flow still works
 * in dev without a backend.
 */
export async function uploadImage(file: File): Promise<UploadedImage> {
  if (useMocks) {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    return delay({ url: dataUrl, bytes: file.size, contentType: file.type });
  }

  const form = new FormData();
  form.append('file', file);

  const headers = new Headers();
  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }
  // Do NOT set Content-Type — the browser must add the multipart boundary

  const response = await fetch(`${baseUrl}/uploads/image`, {
    method: 'POST',
    body: form,
    headers,
  });

  if (response.status === 401) {
    if (unauthorizedHandler) unauthorizedHandler();
    throw new UnauthorizedError();
  }

  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = await response.json();
      if (body && typeof body.message === 'string') message = body.message;
    } catch {
      // not JSON
    }
    throw new Error(message);
  }

  return response.json();
}

// ---- AI photo transformation ---------------------------------------------

/**
 * Starts a Flux Kontext Pro transformation on the given original image URL.
 * Returns immediately with a job in PENDING/PROCESSING — caller polls
 * {@link getAiTransformJob} until status is terminal.
 *
 * Throws {@link Error} with the back's message on 4xx/5xx (notably:
 *   - 429 when the daily quota is exhausted
 *   - 503 when the env doesn't have a Replicate token configured)
 *
 * Mock mode is intentionally unsupported — the only useful test surface is
 * against a real backend, and admin pages already require a logged-in
 * session that mock auth doesn't grant.
 */
export function startAiTransform(input: AiTransformStartInput): Promise<AiTransformJob> {
  if (useMocks) {
    return Promise.reject(
      new Error('AI transformation is not available in mock mode'),
    );
  }
  return apiFetch<AiTransformJob>('/admin/ai/transform', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getAiTransformJob(id: string): Promise<AiTransformJob> {
  if (useMocks) {
    return Promise.reject(
      new Error('AI transformation is not available in mock mode'),
    );
  }
  return apiFetch<AiTransformJob>(`/admin/ai/jobs/${id}`);
}

export function acceptAiTransform(id: string): Promise<AiTransformJob> {
  if (useMocks) {
    return Promise.reject(
      new Error('AI transformation is not available in mock mode'),
    );
  }
  return apiFetch<AiTransformJob>(`/admin/ai/jobs/${id}/accept`, {
    method: 'POST',
  });
}

export function rejectAiTransform(id: string): Promise<AiTransformJob> {
  if (useMocks) {
    return Promise.reject(
      new Error('AI transformation is not available in mock mode'),
    );
  }
  return apiFetch<AiTransformJob>(`/admin/ai/jobs/${id}/reject`, {
    method: 'POST',
  });
}

// ---- AI 4-pose set flow --------------------------------------------------

/**
 * Starts a 4-pose transformation set. One source image → 4 jobs (one per
 * {@link PoseVariant}) spawned in parallel. Returns the set + initial
 * job rows (all PENDING/PROCESSING). Caller polls {@link getAiTransformSet}
 * until every job hits a terminal status.
 *
 * Quota cost: 4 units. Throws 429 up front (no jobs created) if the
 * daily ceiling would be exceeded.
 */
export function startAiTransformSet(
  input: AiTransformSetStartInput,
): Promise<AiTransformSet> {
  if (useMocks) {
    return Promise.reject(
      new Error('AI transformation is not available in mock mode'),
    );
  }
  return apiFetch<AiTransformSet>('/admin/ai/transform-set', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getAiTransformSet(id: string): Promise<AiTransformSet> {
  if (useMocks) {
    return Promise.reject(
      new Error('AI transformation is not available in mock mode'),
    );
  }
  return apiFetch<AiTransformSet>(`/admin/ai/sets/${id}`);
}

/**
 * Most recent set generated from a given source photo, so reopening the AI
 * modal on a product resumes that shoot with every pose still individually
 * regenerable — instead of forcing four fresh generations to redo one image.
 *
 * Resolves to undefined when the photo has never been transformed: the server
 * answers 204 for that, which apiFetch maps to undefined. It is an ordinary
 * state, not an error, and the caller simply offers a first generation.
 */
export function getLatestAiTransformSet(
  originalImageUrl: string,
): Promise<AiTransformSet | undefined> {
  if (useMocks) {
    return Promise.resolve(undefined);
  }
  return apiFetch<AiTransformSet | undefined>(
    `/admin/ai/sets/latest?originalImageUrl=${encodeURIComponent(originalImageUrl)}`,
  );
}

/**
 * Regenerates a single pose within an existing set. Marks the previous
 * job for that pose REJECTED (server-side) and inserts a fresh PENDING
 * job in the same set + same pose. Quota cost: 1 unit.
 */
export function regenerateAiTransformJob(
  id: string,
  /**
   * Full replacement prompt for this rerun only. Omit to replay the prompt
   * the pose already carries; the siblings in the set are never affected.
   */
  prompt?: string,
): Promise<AiTransformJob> {
  if (useMocks) {
    return Promise.reject(
      new Error('AI transformation is not available in mock mode'),
    );
  }
  const body = prompt && prompt.trim() ? JSON.stringify({ prompt }) : undefined;
  return apiFetch<AiTransformJob>(`/admin/ai/jobs/${id}/regenerate`, {
    method: 'POST',
    ...(body ? { body } : {}),
  });
}

/**
 * Aggregated audience figures, read by the API from the report the ops job
 * writes on the VPS. Admin-only.
 *
 * The figures are refreshed once a day, so treat them as a daily snapshot
 * rather than live traffic. 503 means the report has not been generated yet
 * (or is unreadable) — the message from the server says which, and the
 * caller should surface it rather than showing an empty dashboard.
 *
 * Mock mode resolves to undefined: there is no plausible fake for this, and
 * inventing numbers in the offline admin would be worse than showing none.
 */
export function getAudienceReport(): Promise<AudienceReport | undefined> {
  if (useMocks) {
    return Promise.resolve(undefined);
  }
  return apiFetch<AudienceReport>('/admin/analytics');
}
